import { DataLayer } from '@by-miles/data-layer'
import Lottie from 'lottie-react'
import { useContext, useEffect, useRef, useState } from 'react'

import { ProviderContext } from '../../../Provider'
import { PlayAgainButton, PlayNextButton } from '../components/buttons'
import { ExplainerStep } from '../components/ExplainerStep'
import { AnimationSteps, ExplainerProps, TimeoutId } from '../types'
import animationData from './assets/explainer-monthly-how-clips-v2.json'
import { updateExplainerLottieAnimationValues } from './utils/updateExplainerLottieAnimationValues'

export const MonthlyExplainer = ({
  lottieRef,
  activeStep,
  handlePlayStep,
  animationProgress,
  annimationTriggerRef,
}: ExplainerProps) => {
  const { state } = useContext(ProviderContext)
  const timeoutRef = useRef<TimeoutId>(null)
  const quotePricing = state.quotePricing

  const [dynamicAnimationData, setDynamicAnimationData] = useState<any>(null)
  const [isAnimationReady, setIsAnimationReady] = useState(false)

  const handlePlayNext = (step: AnimationSteps) => {
    DataLayer.customEvent({
      eventName: 'monthly animation play next',
      eventType: 'form tracking',
      section: 'how',
    })
    handlePlayStep(step)
  }

  useEffect(() => {
    const {
      estimatedMiles,
      fixedUpfront,
      perMile,
      monthly: { mon },
    } = quotePricing
    try {
      const updatedAnimationData = updateExplainerLottieAnimationValues({
        estimatedMiles,
        perMile,
        fixedUpfront,
        monthlyFixedCost: mon,
        animationData,
      })
      setDynamicAnimationData(updatedAnimationData)

      // Timeout hack required for lottie animation to only mount to the Dom after the dynamic animation data is ready
      // This fixes spacing issue with the dynamic variables - Unable to be done through useState + re render
      timeoutRef.current = setTimeout(() => {
        setIsAnimationReady(true)
      }, 50)
    } catch (error) {
      console.error('Error updating Lottie data:', error)
      setDynamicAnimationData(animationData)
      setIsAnimationReady(true)
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [quotePricing, animationData])

  return (
    <>
      <div className="section">
        {isAnimationReady && (
          <Lottie lottieRef={lottieRef} animationData={dynamicAnimationData} autoplay={false} loop={false} />
        )}
      </div>
      <div className="section">
        <div className="step-wrapper">
          <ExplainerStep
            step={1}
            title="Your fixed cost covers you while parked."
            text="You pay some of this upfront. The rest is spread out over the year. "
            button={<PlayNextButton onClick={() => handlePlayNext(AnimationSteps.Two)} />}
            activeStep={activeStep}
            progress={animationProgress}
          />
          <ExplainerStep
            step={2}
            title="The rest depends on the miles you drive each month."
            text="These are charged at your set per-mile rate."
            button={<PlayNextButton onClick={() => handlePlayNext(AnimationSteps.Three)} />}
            activeStep={activeStep}
            progress={animationProgress}
          />
          <div ref={annimationTriggerRef} />
          <ExplainerStep
            step={3}
            title="So when it comes to cost, you’re at the wheel."
            text="If you drive less, you’ll pay less. But if you need to drive more, that’s fine too."
            button={
              <PlayAgainButton
                onClick={() => {
                  DataLayer.customEvent({
                    eventName: 'monthly animation play again',
                    eventType: 'form tracking',
                    section: 'how',
                  })
                  handlePlayStep(AnimationSteps.One)
                }}
              />
            }
            activeStep={activeStep}
            progress={animationProgress}
          />
        </div>
      </div>
    </>
  )
}
